import { useMailRuntime } from '../context/MailRuntime';
import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useComposerContext } from "../context/ComposerContext";
import { PRESETS } from "../config/presets";
import ColorPicker from "../ui/ColorPicker";
import TabBar from "../ui/TabBar";

const TABS = [
  { key: "style", label: "Style" },
  { key: "headerFooter", label: "Header/Footer" },
  { key: "templates", label: "Templates" },
];

function CollapsibleSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-bookish-purple/8 pb-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-1.5 text-xs font-semibold text-bookish-purple/70 hover:text-bookish-purple transition-colors"
      >
        {title}
        <ChevronDown
          size={13}
          className={`transition-transform duration-200 ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open && <div className="space-y-2 pt-1">{children}</div>}
    </div>
  );
}

function StyleTab({ applyPreset }) {
  const {
    presetKey,
    outerBg, setOuterBg,
    cardBg, setCardBg,
    textColor, setTextColor,
    accentColor, setAccentColor,
    fontFamily, setFontFamily,
    fontSize, setFontSize,
    contentWidth, setContentWidth,
    previewMode,
  } = useComposerContext();

  return (
    <div className="space-y-2">
      <CollapsibleSection title="Brand Preset">
        <select
          value={presetKey}
          onChange={(e) => applyPreset(e.target.value)}
          className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:ring-1 focus:ring-bookish-purple/30 focus:border-bookish-purple/30"
        >
          {PRESETS.map((p) => (
            <option key={p.key} value={p.key}>{p.name}</option>
          ))}
        </select>

      </CollapsibleSection>

      <CollapsibleSection title="Colors">
        <ColorPicker label="Page Background" value={outerBg} onChange={setOuterBg} />
        <ColorPicker label="Content Background" value={cardBg} onChange={setCardBg} />
        <ColorPicker label="Text Color" value={textColor} onChange={setTextColor} />
        <ColorPicker label="Accent Color" value={accentColor} onChange={setAccentColor} />
      </CollapsibleSection>

      <CollapsibleSection title="Typography">
        <div>
          <label className="text-xs block mb-1 text-bookish-purple/60">Font</label>
          <select
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
            className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white"
          >
            <option value="Georgia, serif">Georgia</option>
            <option value="Times New Roman, serif">Times New Roman</option>
            <option value="Arial, sans-serif">Arial</option>
            <option value="Verdana, sans-serif">Verdana</option>
            <option value="Trebuchet MS, sans-serif">Trebuchet MS</option>
          </select>
        </div>
        <div>
          <label className="text-xs block mb-1 text-bookish-purple/60">Text Size</label>
          <select
            value={fontSize}
            onChange={(e) => setFontSize(e.target.value)}
            className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white"
          >
            <option value="12px">Small (12px)</option>
            <option value="14px">Normal (14px)</option>
            <option value="16px">Medium (16px)</option>
            <option value="18px">Large (18px)</option>
            <option value="20px">Extra Large (20px)</option>
            <option value="24px">Heading (24px)</option>
            <option value="28px">Heading+ (28px)</option>
            <option value="32px">Display (32px)</option>
            <option value="36px">Display+ (36px)</option>
          </select>
        </div>
        <div>
          <label className="text-xs block mb-1 text-bookish-purple/60">Content Width</label>
          <select
            value={contentWidth}
            onChange={(e) => setContentWidth(e.target.value)}
            className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white"
            disabled={previewMode === "mobile"}
          >
            <option value="600px">Narrow (600px)</option>
            <option value="700px">Standard (700px)</option>
            <option value="800px">Wide (800px)</option>
          </select>
        </div>
      </CollapsibleSection>
    </div>
  );
}



function HeaderFooterTab({ addHeaderLink, editFooterSocialLinks }) {
  const {
    headerBg, setHeaderBg,
    headerTextColor, setHeaderTextColor,
    headerButtonColor, setHeaderButtonColor,
    headerButtonText, setHeaderButtonText,
    footer, setFooter,
  } = useComposerContext();

  return (
    <div className="space-y-2">
      <CollapsibleSection title="Header settings">
        <div className="grid grid-cols-2 gap-2">
          <ColorPicker label="Background" value={headerBg} onChange={setHeaderBg} />
          <ColorPicker label="Text" value={headerTextColor} onChange={setHeaderTextColor} />
        </div>
        <button onClick={addHeaderLink} className="w-full px-3 py-1.5 text-xs font-medium rounded-lg border border-bookish-purple/15 hover:bg-bookish-purple/5 transition-colors mt-1">+ Add Header Link</button>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <ColorPicker label="Button" value={headerButtonColor} onChange={setHeaderButtonColor} />
          <ColorPicker label="Btn Text" value={headerButtonText} onChange={setHeaderButtonText} />
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="Footer settings">
        <label className="text-xs block mb-1 text-bookish-purple/60">Footer text/content</label>
        <textarea value={footer?.html || ""} onChange={(e)=>setFooter((prev)=>({...(prev||{}), enabled:true, html:e.target.value}))} rows={4} className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-xs bg-white" placeholder="You are receiving this email because you subscribed." />
        <button onClick={editFooterSocialLinks} className="w-full px-3 py-1.5 text-xs font-medium rounded-lg border border-bookish-purple/15 hover:bg-bookish-purple/5 transition-colors mt-1">Edit Footer Social Icons</button>
      </CollapsibleSection>
    </div>
  );
}

function TemplatesTab({ saveCurrentAsTemplate, applyTemplate, deleteTemplate, reloadTemplate }) {
  const runtime = useMailRuntime();
  const {
    templates,
    selectedTemplateId, setSelectedTemplateId,
    newTemplateName, setNewTemplateName,
  } = useComposerContext();

  if (runtime.fixedTemplate) return <div className="space-y-3"><h3 className="font-serif">{runtime.fixedTemplate.name}</h3><p className="text-sm">Save this layout and banner as the reusable template for this newsletter. Saved drafts keep their own copy.</p><button className="min-h-[44px] rounded-lg border px-3" onClick={saveCurrentAsTemplate}>Save newsletter template</button><button className="min-h-[44px] underline" onClick={reloadTemplate}>Start a letter from saved template</button></div>;
  return (
    <div className="space-y-3">
      <select
        aria-label="Saved template"
        value={selectedTemplateId}
        onChange={(e) => setSelectedTemplateId(e.target.value)}
        className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white"
      >
        <option value="">Select a template...</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>

      <div className="flex gap-1.5">
        <button
          onClick={applyTemplate}
          className="flex-1 px-2 py-1.5 text-xs font-medium rounded-lg border border-bookish-purple/15 hover:bg-bookish-purple/5"
        >
          Load
        </button>
        <button
          onClick={deleteTemplate}
          className="px-2 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
        >
          Delete
        </button>
      </div>

      <div className="border-t border-bookish-purple/8 pt-3 space-y-2">
        <p className="text-xs font-medium text-bookish-purple/60">Save Current as Template</p>
        <input
          type="text"
          placeholder="Template name"
          aria-label="Template name"
          maxLength={200}
          value={newTemplateName}
          onChange={(e) => setNewTemplateName(e.target.value)}
          className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white"
        />
        <button
          onClick={saveCurrentAsTemplate}
          className="w-full px-3 py-1.5 text-xs font-medium rounded-lg bg-bookish-purple text-white hover:bg-bookish-purpleAccent transition-colors"
        >
          Save Template
        </button>
      </div>
    </div>
  );
}

export default function DesignSidebar({
  applyPreset,
  addHeaderLink,
  editFooterSocialLinks,
  saveCurrentAsTemplate,
  applyTemplate,
  deleteTemplate,
  reloadTemplate,
  className = "",
  compact = false,
}) {
  const [activeTab, setActiveTab] = useState("style");

  return (
    <aside
      className={`w-72 bg-white border border-bookish-purple/12 rounded-2xl p-3 shadow-sm overflow-y-auto max-h-[calc(100vh-100px)] ${compact ? "max-h-none rounded-none border-0 bg-transparent p-0 shadow-none" : ""} ${className}`.trim()}
    >
      <TabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "style" && (
        <StyleTab applyPreset={applyPreset} />
      )}
      {activeTab === "headerFooter" && (
        <HeaderFooterTab addHeaderLink={addHeaderLink} editFooterSocialLinks={editFooterSocialLinks} />
      )}
      {activeTab === "templates" && (
        <TemplatesTab
          saveCurrentAsTemplate={saveCurrentAsTemplate}
          applyTemplate={applyTemplate}
          deleteTemplate={deleteTemplate} reloadTemplate={reloadTemplate}
        />
      )}
    </aside>
  );
}
